import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { PlusCircle, Save2, Trash, Calendar3, Receipt, Coin, Diagram3, HandThumbsUp, CardList, ListTask } from 'react-bootstrap-icons'
import { Button, Form, Row, Col } from 'react-bootstrap'

import SearchSelect from '../components/form/SearchSelect'
import InputNumber from '../components/form/InputNumber'

import PaymentsPartnersService from '../services/PaymentsPartnersService'
import ProjectsService from '../services/ProjectsService'
import PartnersService from '../services/PartnersService'
import EntitiesService from '../services/EntitiesService'

import { AlertMsg, AlertError, AlertLoading } from '../helpers/AlertHelper'

const PaymentsPartnersForm = (props) => {

    const intl = useIntl()
    const [elementId, setElementId] = useState(props.elementId)
    const [action, setAction] = useState(props.action)
    const [projectId, setProjectId] = useState('')
    const [projectsList, setProjectsList] = useState([])
    const [projectErrorKey, setProjectErrorKey] = useState('')
    const [partnerId, setPartnerId] = useState('')
    const [partnersList, setPartnersList] = useState([])
    const [partnerErrorKey, setPartnerErrorKey] = useState('')
    const [concept, setConcept] = useState('')
    const [amount, setAmount] = useState()
    const [buttonTitle, setButtonTitle] = useState('')
    const [inputDisabled, setInputDisabled] = useState(false)
    const [formValid, setFormValid] = useState(false)

    const [showMsg, setShowMsg] = useState(false)
    const [showAlert, setShowAlert] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(false)

    // Fetch projects
    const fetchProjects = async (ownEntityIds) => {
        try {
            if (!ownEntityIds || ownEntityIds.length === 0) {
                setProjectsList([]) // Si no hay entidades OWN, limpiar la lista de proyectos
                return;
            }

            const response = await ProjectsService.getProjects(1, '', 2000, 'name', 'asc', ownEntityIds)
            setProjectsList(response.data.projects)
        } catch (err) {
            setError(err);
        }
    }

    const fetchEntities = async () => {
        try {
            const response = await EntitiesService.getEntities(1, '', 2000)

            // Filtrar solo las entidades de tipo "OWN"
            const ownEntities = response.data.entities.filter(entity => entity.type === "OWN")

            // Obtener solo los IDs de estas entidades
            const ownEntityIds = ownEntities.map(entity => entity._id)

            return ownEntityIds;
        } catch (err) {
            setError(err)
            return []
        }
    };

    // Fetch partners
    const fetchPartners = async () => {
        try {
            const response = await PartnersService.getPartners(1, '', 2000)
            setPartnersList(response.data.partners)
        } catch (err) {
            setError(err)
        }
    }

    const getData = async () => {
        await PaymentsPartnersService.getPaymentPartner(elementId).then(
            response => {
                setProjectId(response.data.projectId || '')
                setPartnerId(response.data.partnerId || '')
                setConcept(response.data.concept || '')
                setAmount(response.data.amount || 0)
                setIsLoaded(true)
            },
            error => {
                setError(error)
            })
    }

    useEffect(() => {
        const loadData = async () => {
            const ownEntityIds = await fetchEntities()
            await fetchProjects(ownEntityIds)
            await fetchPartners()

            if (elementId) {
                getData()
            }
        }

        loadData()

        switch (action) {
            case 'edit':
                setButtonTitle(<FormattedMessage id="textSave" defaultMessage="Save" />)
                break
            case 'delete':
                setButtonTitle(<FormattedMessage id="textDelete" defaultMessage="Delete" />)
                setInputDisabled(true)
                break
            default:
                setButtonTitle(<FormattedMessage id="textAdd" defaultMessage="Add" />)
        }

    }, [])

    const onSubmit = () => {
        props.onSubmit()
    }

    const handleInputChange = (e) => {
        const name = e.target.name
        const value = e.target.value
        if (name === 'projectId'){
            setProjectErrorKey('')
        } 
        if (name === 'partnerId'){
            setPartnerErrorKey('')
        } 
        setFieldValue(name, value)
    }

    const setFieldValue = (fieldName, value) => {
        switch (fieldName) {
            case 'projectId':
                setProjectId(value)
                break
            case 'partnerId':
                setPartnerId(value)
                break
            case 'concept':
                setConcept(value)
                break
            case 'amount':
                setAmount(value)
                break
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        e.stopPropagation()

        setProjectErrorKey('')
        setPartnerErrorKey('')

        let finalProjectId = projectId
        let finalPartnerId = partnerId

        if (action !== "delete") {
            const matchedProj = projectsList.find(p => p.name.toLowerCase() === (projectId?.toString().toLowerCase() || '') || p._id === projectId)
            if (matchedProj){
                finalProjectId = matchedProj._id
            } 

            const matchedPart = partnersList.find(p => p.name.toLowerCase() === (partnerId?.toString().toLowerCase() || '') || p._id === partnerId)
            if (matchedPart){
                finalPartnerId = matchedPart._id
            } 

            const isProjEmpty = !projectId || projectId.toString().trim() === ''
            const isProjInvalid = !isProjEmpty && !/^[0-9a-fA-F]{24}$/.test(finalProjectId)

            const isPartEmpty = !partnerId || partnerId.toString().trim() === ''
            const isPartInvalid = !isPartEmpty && !/^[0-9a-fA-F]{24}$/.test(finalPartnerId)

            if (isProjEmpty || isProjInvalid || isPartEmpty || isPartInvalid || !e.currentTarget.checkValidity()) {
                setFormValid(true)
                
                if (isProjEmpty){
                    setProjectErrorKey('paymentsPartnersProjectsNotFound')
                } 
                else if (isProjInvalid){
                    setProjectErrorKey('paymentsPartnersProjectsInvalid')
                } 

                if (isPartEmpty){
                    setPartnerErrorKey('paymentsPartnersPartnersNotFound')
                } 
                else if (isPartInvalid){
                    setPartnerErrorKey('paymentsPartnersPartnersInvalid')
                } 
                
                return
            }
        }

        const paymentPartner = {
            projectId: finalProjectId,
            partnerId: finalPartnerId,
            concept,
            amount
        }

        try {
            if (elementId && action === "edit") {
                await PaymentsPartnersService.updatePaymentPartner(paymentPartner, elementId)
            } else if (action === "add") {
                await PaymentsPartnersService.addPaymentPartner(paymentPartner)
            } else if (action === "delete") {
                await PaymentsPartnersService.deletePaymentPartner(elementId)
            }

            onSubmit()
        } catch (error) {
            setShowMsg({ text: error || "An error occurred", style: 'danger' })
            setShowAlert(true)
        }
    }

    if (error) {
        return (
            <AlertError error={error} />
        )
    } else if (elementId && !isLoaded) {
        return (
            <AlertLoading isLoaded={isLoaded} />
        )
    }

    return (
        <div>
            <Form noValidate validated={formValid} onSubmit={handleSubmit}>
                {elementId && (
                    <Form.Group className="mb-3" controlId="id">
                        <Form.Label className='fw-semibold'>Id:</Form.Label>
                        <Form.Control
                            type="text"
                            name="id"
                            value={elementId}
                            disabled={"disabled"}
                        />
                    </Form.Group>
                )}

                <Form.Group className="mb-4" controlId="projectId">
                    <Diagram3 /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="paymentsProject" defaultMessage="Project" />:</Form.Label>

                    <SearchSelect
                        fieldName={'projectId'}
                        selected={projectId}
                        onChange={handleInputChange}
                        options={projectsList}
                        disabled={projectsList.length === 0} 
                        className={projectErrorKey !== '' ? 'is-invalid border-danger' : ''}                       
                        placeholder={projectsList.length === 0 ? intl.formatMessage({ id: "paymentsPartnersProjectsNotFound", defaultMessage: "No projects available" }) : intl.formatMessage({ id: "textClickSelect", defaultMessage: "Click to select or search" })}
                    />
                    {projectErrorKey !== '' && (
                        <div className="text-danger small mt-1">
                            <FormattedMessage id={projectErrorKey} />
                        </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-4" controlId="partnerId">
                    <HandThumbsUp/><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="paymentsPartner" defaultMessage="Partner" />:</Form.Label>

                    <SearchSelect
                        fieldName={'partnerId'}
                        selected={partnerId}
                        onChange={handleInputChange}
                        options={partnersList}
                        disabled={partnersList.length === 0}  
                        className={partnerErrorKey !== '' ? 'is-invalid border-danger' : ''}                      
                        placeholder={partnersList.length === 0 ? intl.formatMessage({ id: "paymentsPartnersPartnersNotFound", defaultMessage: "No partners available" }) : intl.formatMessage({ id: "textClickSelect", defaultMessage: "Click to select or search" })}
                    />
                    {partnerErrorKey !== '' && (
                        <div className="text-danger small mt-1">
                            <FormattedMessage id={partnerErrorKey} />
                        </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-4" controlId="concept">
                    <Receipt /><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="paymentsConcept" defaultMessage="Concept" />:</Form.Label>
                    <Form.Control
                        type="text"
                        name="concept"
                        value={concept}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="paymentsConceptInvalid" defaultMessage="Provide a concept" />
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4" controlId="amount">
                    <Coin/><Form.Label className="fw-semibold mb-2 ms-2"><FormattedMessage id="paymentsAmount" defaultMessage="Amount" />:</Form.Label>
                    <InputNumber
                        fieldName={"amount"}
                        value={amount}
                        onChange={handleInputChange}
                        disabled={inputDisabled && ("disabled")}
                        required="required"
                    />
                    <Form.Control.Feedback type="invalid">
                        <FormattedMessage id="paymentsAmountInvalid" defaultMessage="Provide an amount" />
                    </Form.Control.Feedback>
                </Form.Group>

                <AlertMsg message={showMsg.text} variant={showMsg.style} dismissible onCloseHandler={() => setShowAlert(false)} show={showAlert} />

                <div className="d-grid gap-2">
                    {action === "add" && (
                        <Button
                            className="btn btn-dark fw-semibold"
                            type="submit"
                        >
                            <PlusCircle /> {buttonTitle}
                        </Button>
                    )}

                    {action === "edit" && (
                        <Button
                            className="btn btn-primary fw-semibold"
                            type="submit"
                        >
                            <Save2 /> {buttonTitle}
                        </Button>
                    )}

                    {action === "delete" && (
                        <Button
                            className="btn btn-danger fw-semibold"
                            type="submit"
                        >
                            <Trash /> {buttonTitle}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    )

}

export default PaymentsPartnersForm